/**
 * This module contains plan (IEP/504/IPP) operations that need client-side coordination spanning
 * more than one API call.
 *
 * Its current concern is read-after-write consistency. A wizard step’s Next button is a NavLink,
 * so React Router navigates synchronously on click while the step’s fire-and-forget review-status
 * PATCH is still in flight. The next step then mounts and issues its own GET, which can land
 * first and yield a plan whose `reviewStatus` values are already stale—making a section that the
 * user just finished look incomplete in the rundown tallies.
 *
 * The wrappers below close that race from the read side: writes register themselves against their
 * plan’s ID, and a read holds until any writes already in flight for that plan have settled. That
 * leaves the components’ navigation and their fire-and-forget handlers exactly as they are.
 *
 * IMPORTANT: components dealing with plans of any type (IEP/504/IPP) must import these functions
 * rather than their same-named counterparts in the api module—the api ones bypass the barrier
 * silently.
 */
import * as api from '../api'
import { PLAN_TYPE_504, PLAN_TYPE_IEP, PLAN_TYPE_IPP, REVIEW_STATUS_REVIEWED } from '../arch/iepConstants'

// Upper bound on how long a read will wait for pending writes. A write that hangs should delay a
// page load, not strand it: on expiration the read proceeds anyway, which is the behavior we had
// before the barrier existed.
const MAX_WAIT_MS = 5000

// Maps a plan ID to the set of writes currently in flight for that plan.
const pendingWrites = new Map()

/**
 * Registers a plan write so that subsequent reads of the same plan wait for it.
 *
 * @param {string} planId the ID of the plan being written to
 * @param {Promise} writePromise the promise for the write that is now in flight
 * @returns the given promise, unchanged, so callers see the same result and the same errors
 */
const trackPlanWrite = (planId, writePromise) => {
  // The barrier only cares that a write is *done*, not whether it succeeded, so it waits on a
  // promise that never rejects. Failures remain the caller’s to handle, via the returned promise.
  const settledWrite = writePromise.catch(() => {})

  const writes = pendingWrites.get(planId) ?? new Set()
  writes.add(settledWrite)
  pendingWrites.set(planId, writes)

  settledWrite.then(() => {
    writes.delete(settledWrite)
    if (writes.size === 0) {
      pendingWrites.delete(planId)
    }
  })

  return writePromise
}

/**
 * Waits for every write currently in flight for the given plan.
 *
 * Writes can beget writes—a handler may await one PATCH before issuing the next—so this drains
 * repeatedly rather than settling for a single pass.
 */
const drainPlanWrites = async planId => {
  let writes = pendingWrites.get(planId)

  while (writes?.size) {
    await Promise.all([...writes])
    writes = pendingWrites.get(planId)
  }
}

/**
 * Holds until the given plan has no writes in flight, or until MAX_WAIT_MS elapses.
 *
 * @param {string} planId the ID of the plan about to be read
 */
const awaitPlanWrites = async planId => {
  if (!pendingWrites.get(planId)?.size) {
    return
  }

  let expirationTimeout
  const expiration = new Promise(resolve => {
    expirationTimeout = setTimeout(resolve, MAX_WAIT_MS)
  })

  try {
    await Promise.race([drainPlanWrites(planId), expiration])
  } finally {
    clearTimeout(expirationTimeout)
  }
}

// The optional read options are forwarded verbatim; see api.getIep for what they carry. The
// barrier’s job is ordering, not filtering—a wrapper that quietly drops arguments turns the very
// indirection this module’s docstring mandates into a trap, with no error anywhere to show for it.
const getIep = async (id, options) => {
  await awaitPlanWrites(id)
  return api.getIep(id, options)
}

const getIepExtractionData = async id => {
  await awaitPlanWrites(id)
  return api.getSchoolPlanDocumentExtractionData(id)
}

// The extraction PATCH re-derives the plan's item records server-side, so any plan read issued
// right after it must wait on it like any other plan write.
const patchIepExtractionData = (id, data) => trackPlanWrite(id, api.patchIepExtractionData(id, data))

const patchIep = (id, payload) => trackPlanWrite(id, api.patchIep(id, payload))

const postIepConcern = (id, payload) => trackPlanWrite(id, api.postIepConcern(id, payload))

const patchIepConcern = (id, payload) => trackPlanWrite(id, api.patchIepConcern(id, payload))

const patchIepRecommendationStatus = (iepId, recommendationId, statusPayload) =>
  trackPlanWrite(iepId, api.patchIepRecommendationStatus(iepId, recommendationId, statusPayload))

// The API has no bulk recommendation patch: the server’s bulk dispatcher covers goals, plops,
// accommodations, and services only, so a PATCH to the recommendations collection 404s. This fans out
// over the single-recommendation endpoint instead, while keeping the signature and payload shape of
// its sibling batch patchers—so adopting a real bulk endpoint later changes only this body. The whole
// fan-out registers as one write, so a following read waits for all of it.
const patchIepRecommendationReviewStatuses = (iepId, recommendationIdsWithStatuses) =>
  trackPlanWrite(
    iepId,
    Promise.all(
      Object.entries(recommendationIdsWithStatuses).map(([recommendationId, statusPayload]) =>
        api.patchIepRecommendationStatus(iepId, recommendationId, statusPayload)
      )
    )
  )

const patchIepGoalReviewStatus = (iepId, goalId, reviewStatus) =>
  trackPlanWrite(iepId, api.patchIepGoalReviewStatus(iepId, goalId, reviewStatus))

const patchIepGoalReviewStatuses = (iepId, goalIdsWithStatuses) =>
  trackPlanWrite(iepId, api.patchIepGoalReviewStatuses(iepId, goalIdsWithStatuses))

const patchIepServiceReviewStatus = (iepId, serviceId, reviewStatus) =>
  trackPlanWrite(iepId, api.patchIepServiceReviewStatus(iepId, serviceId, reviewStatus))

const patchIepServiceReviewStatuses = (iepId, serviceIdsWithStatuses) =>
  trackPlanWrite(iepId, api.patchIepServiceReviewStatuses(iepId, serviceIdsWithStatuses))

// Note that these accommodation functions also serve modifications, which are accommodations
// server-side (distinguished only by their `type`).
const patchIepAccommodationReviewStatus = (iepId, accommodationId, reviewStatus) =>
  trackPlanWrite(iepId, api.patchIepAccommodationReviewStatus(iepId, accommodationId, reviewStatus))

const patchIepAccommodationReviewStatuses = (iepId, accommodationIdsWithStatuses) =>
  trackPlanWrite(iepId, api.patchIepAccommodationReviewStatuses(iepId, accommodationIdsWithStatuses))

const patchIepPlopReviewStatus = (iepId, plopId, reviewStatus) =>
  trackPlanWrite(iepId, api.patchIepPlopReviewStatus(iepId, plopId, reviewStatus))

// See getIep on the forwarded options.
const getIpp = async (id, options) => {
  await awaitPlanWrites(id)
  return api.getIpp(id, options)
}

const getIppExtractionData = async id => {
  await awaitPlanWrites(id)
  return api.getIppExtractionData(id)
}

const patchIppExtractionData = (id, data) => trackPlanWrite(id, api.patchIppExtractionData(id, data))

const postIppConcern = (id, payload) => trackPlanWrite(id, api.postIppConcern(id, payload))

const patchIppConcern = (id, payload) => trackPlanWrite(id, api.patchIppConcern(id, payload))

const patchIppRecommendationStatus = (ippId, recommendationId, statusPayload) =>
  trackPlanWrite(ippId, api.patchIppRecommendationStatus(ippId, recommendationId, statusPayload))

// See patchIepRecommendationReviewStatuses for why this fans out rather than issuing one request.
const patchIppRecommendationReviewStatuses = (ippId, recommendationIdsWithStatuses) =>
  trackPlanWrite(
    ippId,
    Promise.all(
      Object.entries(recommendationIdsWithStatuses).map(([recommendationId, statusPayload]) =>
        api.patchIppRecommendationStatus(ippId, recommendationId, statusPayload)
      )
    )
  )

const patchIppPriorityReviewStatus = (ippId, priorityId, reviewStatus) =>
  trackPlanWrite(ippId, api.patchIppPriorityReviewStatus(ippId, priorityId, reviewStatus))

const patchIppPriorityReviewStatuses = (ippId, priorityIdsWithStatuses) =>
  trackPlanWrite(ippId, api.patchIppPriorityReviewStatuses(ippId, priorityIdsWithStatuses))

const patchIppOutcomeReviewStatus = (ippId, outcomeId, reviewStatus) =>
  trackPlanWrite(ippId, api.patchIppOutcomeReviewStatus(ippId, outcomeId, reviewStatus))

const patchIppOutcomeReviewStatuses = (ippId, outcomeIdsWithStatuses) =>
  trackPlanWrite(ippId, api.patchIppOutcomeReviewStatuses(ippId, outcomeIdsWithStatuses))

const patchIppGoalReviewStatus = (ippId, goalId, reviewStatus) =>
  trackPlanWrite(ippId, api.patchIppGoalReviewStatus(ippId, goalId, reviewStatus))

const patchIppGoalReviewStatuses = (ippId, goalIdsWithStatuses) =>
  trackPlanWrite(ippId, api.patchIppGoalReviewStatuses(ippId, goalIdsWithStatuses))

const patchIppServiceReviewStatus = (ippId, serviceId, reviewStatus) =>
  trackPlanWrite(ippId, api.patchIppServiceReviewStatus(ippId, serviceId, reviewStatus))

const patchIppServiceReviewStatuses = (ippId, serviceIdsWithStatuses) =>
  trackPlanWrite(ippId, api.patchIppServiceReviewStatuses(ippId, serviceIdsWithStatuses))

const patchIppSupportReviewStatus = (ippId, supportId, reviewStatus) =>
  trackPlanWrite(ippId, api.patchIppSupportReviewStatus(ippId, supportId, reviewStatus))

const patchIppSupportReviewStatuses = (ippId, supportIdsWithStatuses) =>
  trackPlanWrite(ippId, api.patchIppSupportReviewStatuses(ippId, supportIdsWithStatuses))

// Maps each plan type to the concern/recommendation functions that RecommendationsAndConcerns needs
// injected (via ConcernsContainer), named for the props they fill. Components serving more than one
// plan type should look their functions up here rather than importing them directly, so that writes
// go to the right endpoints. 504 plans are IEPs in this regard.
const IEP_CONCERN_FUNCTIONS = {
  concernPoster: postIepConcern,
  concernPatcher: patchIepConcern,
  recommendationStatusPatcher: patchIepRecommendationStatus
}

const PLAN_CONCERN_FUNCTIONS = {
  [PLAN_TYPE_IEP]: IEP_CONCERN_FUNCTIONS,
  [PLAN_TYPE_504]: IEP_CONCERN_FUNCTIONS,

  [PLAN_TYPE_IPP]: {
    concernPoster: postIppConcern,
    concernPatcher: patchIppConcern,
    recommendationStatusPatcher: patchIppRecommendationStatus
  }
}

// Maps each plan type to its extraction-editing endpoints. A plan type absent from this table has
// no extraction behind it, and extraction editing stays off for it entirely. 504 plans are IEPs in
// this regard.
const IEP_EXTRACTION_FUNCTIONS = {
  extractionGetter: getIepExtractionData,
  extractionPatcher: patchIepExtractionData
}

const PLAN_EXTRACTION_FUNCTIONS = {
  [PLAN_TYPE_IEP]: IEP_EXTRACTION_FUNCTIONS,
  [PLAN_TYPE_504]: IEP_EXTRACTION_FUNCTIONS,

  [PLAN_TYPE_IPP]: {
    extractionGetter: getIppExtractionData,
    extractionPatcher: patchIppExtractionData
  }
}

// Maps each plan type to its print endpoints (PDF report generation + HTML preview + save-to-binder),
// by binding api.generatePlanReportForPrint/previewPlanReport/savePlanReportToBinder to the right
// resource segment ('ieps' vs 'ipps')—IPPs post to /ipps/<id>/... instead of the IEP/504
// /ieps/<id>/... routes. Callers (PrintDialog, useIepPrint) look their functions up here by planType
// rather than picking a resource themselves, so the print dialog serves all three plan types from one
// call site. 504 plans are IEPs in this regard.
const makePrintFunctions = resource => ({
  reportGenerator: (id, payload) => api.generatePlanReportForPrint(resource, id, payload),
  reportPreviewer: (id, payload) => api.previewPlanReport(resource, id, payload),
  reportSaveToBinder: (id, payload) => api.savePlanReportToBinder(resource, id, payload)
})

const IEP_PRINT_FUNCTIONS = makePrintFunctions('ieps')

// The batch recommendation review-status patcher for each plan type.
const RECOMMENDATION_REVIEW_STATUSES_PATCHERS = {
  [PLAN_TYPE_IEP]: patchIepRecommendationReviewStatuses,
  [PLAN_TYPE_504]: patchIepRecommendationReviewStatuses,
  [PLAN_TYPE_IPP]: patchIppRecommendationReviewStatuses
}

/**
 * Marks the given recommendations reviewed, as a page that hosts loose recommendations (a section’s
 * or the general ones) does when the user moves on from it. Only the ones not yet reviewed are
 * patched, since the patchers fan out over one request apiece, and with none of those there is no
 * request at all.
 *
 * @param {object} plan the plan holding the recommendations; its ID and type pick the patcher
 * @param {array} recommendations the recommendations to mark reviewed, reviewed ones included
 * @returns a promise that settles when the patches do
 */
const patchPlanRecommendationsReviewed = (plan, recommendations = []) => {
  const { id: planId, planType } = plan ?? {}
  const patcher = RECOMMENDATION_REVIEW_STATUSES_PATCHERS[planType]

  const payload = Object.fromEntries(
    recommendations
      .filter(({ reviewStatus }) => reviewStatus !== REVIEW_STATUS_REVIEWED)
      .map(({ id }) => [id, { reviewStatus: REVIEW_STATUS_REVIEWED }])
  )

  return patcher && Object.keys(payload).length > 0 ? patcher(planId, payload) : Promise.resolve()
}

const PLAN_PRINT_FUNCTIONS = {
  [PLAN_TYPE_IEP]: IEP_PRINT_FUNCTIONS,
  [PLAN_TYPE_504]: IEP_PRINT_FUNCTIONS,

  [PLAN_TYPE_IPP]: makePrintFunctions('ipps')
}

export {
  MAX_WAIT_MS,
  PLAN_CONCERN_FUNCTIONS,
  PLAN_EXTRACTION_FUNCTIONS,
  PLAN_PRINT_FUNCTIONS,
  getIep,
  getIepExtractionData,
  patchIepExtractionData,
  getIppExtractionData,
  patchIppExtractionData,
  patchIep,
  postIepConcern,
  patchIepConcern,
  patchIepRecommendationStatus,
  patchIepRecommendationReviewStatuses,
  patchIepGoalReviewStatus,
  patchIepGoalReviewStatuses,
  patchIepServiceReviewStatus,
  patchIepServiceReviewStatuses,
  patchIepAccommodationReviewStatus,
  patchIepAccommodationReviewStatuses,
  patchIepPlopReviewStatus,
  getIpp,
  postIppConcern,
  patchIppConcern,
  patchIppRecommendationStatus,
  patchIppRecommendationReviewStatuses,
  patchIppPriorityReviewStatus,
  patchIppPriorityReviewStatuses,
  patchIppOutcomeReviewStatus,
  patchIppOutcomeReviewStatuses,
  patchIppGoalReviewStatus,
  patchIppGoalReviewStatuses,
  patchIppServiceReviewStatus,
  patchIppServiceReviewStatuses,
  patchPlanRecommendationsReviewed,
  patchIppSupportReviewStatus,
  patchIppSupportReviewStatuses
}
